import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Spinner } from "../../../../components/ui";
import { FETCH_COMPANIES as fetchCompanies } from "../../../../graphql/query/Company";
import { FETCH_INVOICE_ID_BY_REFERENCE as fetchInvoiceIdByReference } from "../../../../graphql/query/Invoice";
import {
  findCompanyIdByInvoiceCode,
  parseInvoicePrefix,
  parseInvoiceReference,
} from "../../../../util/parseInvoiceReference";
import { invoiceFilterStore } from "../../components/useInvoiceFilters";
import Wizard from "./index";

// Invoice ids in the URL are numeric. A non-numeric segment (e.g. "WCA19105")
// is treated as an invoice reference and resolved to the real id.
export const isNumericInvoiceId = value => /^\d+$/.test(String(value ?? ""));

// Target detail URL once a reference has been resolved to a numeric id. Any
// sub-path (e.g. /invoice/WCA19105/costs) is preserved; otherwise we land on
// the details tab.
export const buildInvoiceTargetPath = (id, subPath) =>
  subPath ? `/invoice/${id}/${subPath}` : `/invoice/${id}/details`;

// Invoice search list, used when a reference resolves to more than one match.
export const buildInvoiceSearchPath = () => "/invoice";

// Turn a reference like "WCA19105" into the server filter that finds it: the
// digits are the invoice number and the "WCA" prefix is a company's
// invoiceCode, which scopes the search so the number cannot collide with the
// same number at another company. Returns null when the reference carries no
// digits (nothing to look up).
export const buildInvoiceReferenceFilter = (reference, companies = []) => {
  const number = parseInvoiceReference(reference);
  if (number === null) return null;

  const companyId = findCompanyIdByInvoiceCode(
    companies,
    parseInvoicePrefix(reference),
  );

  return companyId == null
    ? { number: { value: number, operator: "eq" } }
    : {
        number: { value: number, operator: "eq" },
        companyId: { value: [companyId], operator: "in" },
      };
};

// Decide where a `/invoice/<reference>` URL should land once the reference has
// been resolved. Exactly one match redirects straight to that invoice; more
// than one sends the user to the search list (pre-filtered by reference);
// anything else is treated as "not found".
export const resolveInvoiceReferenceDestination = ({
  count,
  matchId,
  subPath,
} = {}) => {
  if (count === 1 && matchId != null) {
    return { type: "invoice", to: buildInvoiceTargetPath(matchId, subPath) };
  }

  if (count > 1) {
    return { type: "search", to: buildInvoiceSearchPath() };
  }

  return { type: "notFound" };
};

const InvoiceReferenceRedirect = ({ reference, subPath }) => {
  const navigate = useNavigate();

  const { data: companiesData, loading: companiesLoading } =
    useQuery(fetchCompanies());
  const companies = companiesData?.companies?.edges;

  const filter = useMemo(
    () => buildInvoiceReferenceFilter(reference, companies || []),
    [reference, companies],
  );

  const { data, loading } = useQuery(fetchInvoiceIdByReference, {
    variables: { input: { ...(filter || {}), limit: 2 } },
    skip: companiesLoading || filter === null,
  });

  const count = data?.invoicesV2?.count;
  const matchId = data?.invoicesV2?.edges?.[0]?.id;

  const destination = companiesLoading || loading
    ? { type: "loading" }
    : filter === null
      ? { type: "notFound" }
      : resolveInvoiceReferenceDestination({ count, matchId, subPath });

  useEffect(() => {
    if (destination.type === "search") {
      // Seed the shared filter so the list shows only the matching references.
      invoiceFilterStore.set(filter);
      navigate(destination.to, { replace: true });
    }
  }, [destination.type, destination.to, navigate, filter]);

  if (destination.type === "loading" || destination.type === "search") {
    return (
      <div className="flex min-h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (destination.type === "notFound") {
    return (
      <div className="flex min-h-full w-full items-center justify-center">
        <div className="text-red-600">
          {`No invoice found for reference “${reference}”.`}
        </div>
      </div>
    );
  }

  return <Navigate to={destination.to} replace />;
};

// Routed at `:id/*`. Numeric ids render the invoice wizard directly; a
// reference is looked up and redirected to its numeric detail page.
const WizardRoute = props => {
  const params = useParams();
  const reference = params?.id;
  const subPath = params?.["*"];

  if (isNumericInvoiceId(reference)) {
    return <Wizard {...props} />;
  }

  return <InvoiceReferenceRedirect reference={reference} subPath={subPath} />;
};

export default WizardRoute;
